/**
 * Sync ARIA radio state for a single-select option list using the
 * "roving tabindex" pattern (WAI-ARIA APG).
 * Reference:
 * https://www.w3.org/WAI/ARIA/apg/patterns/radio/examples/radio/
 *
 * @param {Element[]|NodeListOf<Element>|null|undefined} items
 * List item elements (or radio elements) to sync.
 * @param {Element|null|undefined} activeItem
 * Optional active item; if omitted, the first item with the `.active` class is used.
 * @param {string|null|undefined} buttonSelector
 * Selector for the radio control within each item. If falsy, defaults to 'button'.
 * @param {boolean} tabAllVisible
 * If true, sets tabindex="0" on all visible radios (keeps tabindex="-1" for hidden ones).
 * @returns {void}
 */
export default function updateSingleSelectAria(items, activeItem, buttonSelector = 'button', tabAllVisible = false) {
    const list = items || [];
    if (!list.length) {
        return;
    }

    const active = activeItem || list.find((i) => i.classList.contains('active'));

    const selector = buttonSelector || 'button';

    list.forEach((i) => {
        const btn = i.matches?.(selector) ? i : i.querySelector(selector);
        if (!btn) {
            return;
        }

        if (i.classList.contains('hide') || i.closest?.('.hide')) {
            btn.setAttribute('aria-checked', 'false');
            btn.setAttribute('tabindex', '-1');
            return;
        }

        const isActive = i === active;
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');

        let tabIndex = '-1';
        if (tabAllVisible || isActive) {
            tabIndex = '0';
        }
        btn.setAttribute('tabindex', tabIndex);
    });
}
